import React, { useState, useContext, useRef, useEffect } from 'react';
import { Form, InputGroup, Spinner, ListGroup, Badge } from 'react-bootstrap';
import { Search, X } from 'lucide-react';
import AuthContext from '../../context/AuthContext';
import useHttpRequest from '../../hooks/useHttpRequest';
import './NlqQuickSearch.css';

const NlqQuickSearch = ({ onResultSelect, placeholder = "Quick search..." }) => {
    const { clinicId } = useContext(AuthContext);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef(null);

    const { isLoading, error, sendRequest: fetchQuickSearch } = useHttpRequest();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced search
    useEffect(() => {
        if (!query.trim() || query.length < 2) {
            setResults([]);
            setShowResults(false);
            return;
        }

        const debounceTimer = setTimeout(() => {
            performSearch();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [query]);

    const performSearch = () => {
        const transformData = (data) => {
            setResults(data?.results || data || []);
            setShowResults(true);
        };

        fetchQuickSearch(
            {
                url: `/nlq/quick-search?term=${encodeURIComponent(query)}&clinicId=${clinicId}`,
                method: 'GET'
            },
            transformData
        );
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
    };

    const handleResultClick = (result) => {
        if (onResultSelect) {
            onResultSelect(result);
        }
        setShowResults(false);
        setQuery('');
    };

    const handleClear = () => {
        setQuery('');
        setResults([]);
        setShowResults(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setShowResults(false);
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (query.trim()) {
                performSearch();
            }
        }
    };

    return (
        <div className="nlq-quick-search" ref={searchRef}>
            <InputGroup>
                <InputGroup.Text className="search-icon">
                    {isLoading ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        <Search size={18} />
                    )}
                </InputGroup.Text>
                <Form.Control
                    type="text"
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => results.length > 0 && setShowResults(true)}
                    className="search-input"
                />
                {query && (
                    <InputGroup.Text
                        className="clear-icon"
                        onClick={handleClear}
                        style={{ cursor: 'pointer' }}
                    >
                        <X size={18} />
                    </InputGroup.Text>
                )}
            </InputGroup>

            {showResults && (
                <div className="search-results-dropdown">
                    {error && (
                        <div className="search-error p-3 text-danger">
                            {error}
                        </div>
                    )}

                    {!error && results.length === 0 && !isLoading && query.length >= 2 && (
                        <div className="no-results p-3 text-muted text-center">
                            No results found for "{query}"
                        </div>
                    )}

                    {results.length > 0 && (
                        <ListGroup variant="flush">
                            {results.map((result, index) => (
                                <ListGroup.Item
                                    key={index}
                                    action
                                    onClick={() => handleResultClick(result)}
                                    className="search-result-item"
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="result-title">
                                                {result.name || result.title || result.label ||
                                                    Object.values(result)[0]}
                                            </div>
                                            {result.type && (
                                                <small className="text-muted">
                                                    {result.description || result.subtitle || ''}
                                                </small>
                                            )}
                                        </div>
                                        {result.type && (
                                            <Badge bg="secondary" className="result-type-badge">
                                                {result.type}
                                            </Badge>
                                        )}
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </div>
            )}
        </div>
    );
};

export default NlqQuickSearch;